import { z } from 'zod';

import { BaseWidgetSchema } from './BaseWidgetSchema';
import { ProseMirrorJSONSchema } from './ProseMirrorJSONSchema';
import { MOE_QUESTION_TYPES } from '../../../xapi-events/utils/moeQuestionTypes';
import { WidgetType } from '../../utils/widgetsUtils';

export enum QuestionLayoutStyle {
  Default = 'default',
  Horizontal = 'horizontal',
  Vertical = 'vertical'
}

export enum Feedbacks {
  Automatic = 'automatic',
  Check = 'check',
  None = 'none'
}

export enum QuestionScoreMode {
  WithScore = 'withScore',
  WithoutScore = 'withoutScore'
}

export enum EvaluationMode {
  Auto = 'auto',
  Manual = 'manual'
}

export const BaseQuestionWidgetSchema = <T extends WidgetType>(widgetType: T) =>
  BaseWidgetSchema(widgetType).extend({
    /**
     * ProseMirror JSON
     */
    detailedFeedbackFragment: ProseMirrorJSONSchema.optional(),
    evaluationMode: z.nativeEnum(EvaluationMode).optional(),
    feedback: z.nativeEnum(Feedbacks),
    /**
     * ProseMirror JSON
     */
    hintFragment: ProseMirrorJSONSchema,
    /**
     * ProseMirror JSON
     */
    instructionsFragment: ProseMirrorJSONSchema,
    /**
     * ProseMirror JSON
     */
    labelFragment: ProseMirrorJSONSchema,
    layout: z.record(
      z.string(),
      z.object({
        height: z.union([z.number(), z.string()]),
        width: z.number(),
        isDeletable: z.boolean(),
        isDraggable: z.boolean()
      })
    ),
    layoutStyle: z.nativeEnum(QuestionLayoutStyle).optional(),
    /**
     * The MOE (720) question type this question reports as, when its author declared one. OPTIONAL, and
     * sparse on purpose: absence means "follow the widget's own mapping", which is what nearly all content
     * says and what `getMoeQuestionType` answers. Making it required — or backfilling it — would both break
     * every existing question here at parse time and freeze today's mapping against MOE's next correction.
     */
    moeQuestionType: z.enum(MOE_QUESTION_TYPES).optional(),
    /**
     * Per list, stored id → sent id (`componentIdOverrides.ts`). Must be declared or validation strips it. Lenient
     * on purpose: a malformed value must not fail the question; `resolveComponentIdOverrides` validates entries.
     */
    componentIdOverrides: z.record(z.string(), z.record(z.string(), z.unknown())).optional().catch(undefined),
    /** Per list, stored id → sent description (`componentDescriptionOverrides.ts`); lenient for the same reason. */
    componentDescriptionOverrides: z.record(z.string(), z.record(z.string(), z.unknown())).optional().catch(undefined),
    questionScoreMode: z.nativeEnum(QuestionScoreMode),
    showAnswerButton: z.boolean(),
    showDetailedFeedback: z.boolean().optional(),
    showFeedback: z.boolean(),
    showGeneralGrade: z.boolean().optional(),
    showHintButton: z.boolean(),
    showIndicatorButton: z.boolean(),
    showInstructions: z.boolean(),
    showHeadline: z.boolean().optional()
  });
